import React, { useState, useEffect, useRef } from 'react';
import { driverFilesApi, driversApi, reportsApi, driverApplicationsApi, aiApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  FolderOpen,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Shield,
  Car,
  Stethoscope,
  GraduationCap,
  File,
  Mail,
  Download as DownloadIcon,
  Phone,
  MapPin,
  DollarSign,
  Truck,
  ClipboardCopy,
  Eye,
  UserPlus,
  ExternalLink,
  Share2,
  Sparkles,
  Loader2,
  MessageSquare,
  Link2,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { DragDropZone } from '../components/DragDropZone';

const FILE_TYPES = [
  { value: 'application', label: 'Employment Application', icon: FileText },
  { value: 'psp_report', label: 'DOT PSP Report', icon: Shield },
  { value: 'mvr_report', label: 'Motor Vehicle Report (MVR)', icon: Car },
  { value: 'medical_card', label: 'Medical Card', icon: Stethoscope },
  { value: 'license', label: 'CDL License', icon: User },
  { value: 'drug_test', label: 'Drug Test', icon: FileText },
  { value: 'training', label: 'Training Certificate', icon: GraduationCap },
  { value: 'other', label: 'Other Document', icon: File },
];

export default function DriverFiles() {
  const { canEdit, isAdmin, isDriver, user, canUploadFiles } = useAuth();
  const [files, setFiles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [quickUploadType, setQuickUploadType] = useState(null);
  const fileInputRef = useRef(null);
  const quickUploadRef = useRef(null);

  // Driver details dialog state
  const [driverDetailsOpen, setDriverDetailsOpen] = useState(false);
  const [selectedDriverDetails, setSelectedDriverDetails] = useState(null);

  // Applications state
  const [applications, setApplications] = useState([]);
  const [appLoading, setAppLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDetailOpen, setAppDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('files');
  const [appStatusFilter, setAppStatusFilter] = useState('all');

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // AI Upload state for applications
  const [aiUploadDialogOpen, setAiUploadDialogOpen] = useState(false);
  const [extractingInfo, setExtractingInfo] = useState(false);

  const openDriverDetails = (driver) => {
    setSelectedDriverDetails(driver);
    setDriverDetailsOpen(true);
  };

  const [formData, setFormData] = useState({
    driver_id: '',
    file_type: 'application',
    title: '',
    description: '',
    expiration_date: '',
    file_id: null,
    file_name: null,
  });

  useEffect(() => {
    loadData();
  }, [selectedDriver]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [driversRes, filesRes] = await Promise.all([
        driversApi.getAll(),
        driverFilesApi.getAll(selectedDriver !== 'all' ? { driver_id: selectedDriver } : {}),
      ]);
      setDrivers(driversRes.data);
      setFiles(filesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    if (isDriver) return;
    setAppLoading(true);
    try {
      const res = await driverApplicationsApi.getAll(appStatusFilter !== 'all' ? appStatusFilter : null);
      setApplications(res.data);
    } catch (error) {
      console.error('Failed to load applications');
    } finally {
      setAppLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'applications') {
      loadApplications();
    }
  }, [activeTab, appStatusFilter]);

  const handleUpdateAppStatus = async (appId, newStatus) => {
    try {
      await driverApplicationsApi.updateStatus(appId, newStatus);
      toast.success(`Application status updated to ${newStatus}`);
      loadApplications();
      if (selectedApp?.id === appId) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await driverApplicationsApi.delete(appId);
      toast.success('Application deleted');
      loadApplications();
      if (selectedApp?.id === appId) {
        setAppDetailOpen(false);
        setSelectedApp(null);
      }
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const copyApplicationLink = () => {
    const link = `${window.location.origin}/apply`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast.success('Application link copied to clipboard!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const applicationLink = `${window.location.origin}/apply`;

  const handleShareViaEmail = () => {
    const subject = encodeURIComponent('Driver Application - Apply Now');
    const body = encodeURIComponent(`You are invited to submit your driver application. Please fill out the form at the following link:\n\n${applicationLink}\n\nThank you!`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleShareViaSMS = () => {
    const body = encodeURIComponent(`You're invited to apply as a driver! Fill out the application here: ${applicationLink}`);
    window.open(`sms:?body=${body}`);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Driver Application',
          text: 'Apply to become a driver with us!',
          url: applicationLink,
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      copyApplicationLink();
    }
  };

  const handleAIApplicationUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setExtractingInfo(true);
    try {
      const res = await aiApi.extractDriverInfo(file);
      const extracted = res.data.extracted_data;

      if (extracted.error) {
        toast.error(extracted.error);
        return;
      }

      // Auto-create driver from extracted data
      const driverData = {
        name: extracted.name || 'Unknown',
        email: extracted.email || '',
        phone: extracted.phone || '',
        street_address: extracted.street_address || '',
        city: extracted.city || '',
        state: extracted.state || '',
        zipcode: extracted.zipcode || '',
        license_number: extracted.license_number || '',
        license_expiry: extracted.license_expiry || '',
        ssn_last_four: extracted.ssn_last_four || '',
        status: 'active',
        assigned_unit_id: 'none',
      };

      const driverRes = await driversApi.create(driverData);
      const newDriverId = driverRes.data.id;

      // Save the uploaded file as a driver file record
      if (res.data.file_id) {
        await driverFilesApi.create({
          driver_id: newDriverId,
          file_type: 'application',
          title: `Application - ${extracted.name || 'Uploaded'} - ${new Date().toLocaleDateString()}`,
          description: 'Uploaded via AI extraction',
          expiration_date: '',
          file_id: res.data.file_id,
          file_name: res.data.file_name,
        });
      }

      toast.success(`Driver "${driverData.name}" created from uploaded application!`);
      setAiUploadDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract driver information');
    } finally {
      setExtractingInfo(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      in_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('File must be smaller than 100MB');
      return;
    }

    setUploading(true);
    try {
      const res = await driverFilesApi.uploadDocument(file);
      setFormData({
        ...formData,
        file_id: res.data.file_id,
        file_name: res.data.filename,
      });
      setSelectedDocument({ name: res.data.filename, size: res.data.size });
      toast.success('Document uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFile) {
        await driverFilesApi.update(editingFile.id, formData);
        toast.success('File record updated');
      } else {
        await driverFilesApi.create(formData);
        toast.success('File record created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file record?')) return;
    try {
      await driverFilesApi.delete(id);
      toast.success('File record deleted');
      loadData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await driverFilesApi.sendReminders();
      toast.success(`Reminders sent! ${res.data.expired_count} expired, ${res.data.expiring_count} expiring`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleDownloadReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await reportsApi.driverCompliance();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Driver_Compliance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const res = await driverFilesApi.downloadDocument(fileId);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const resetForm = () => {
    setEditingFile(null);
    setSelectedDocument(null);
    setFormData({
      driver_id: '',
      file_type: 'application',
      title: '',
      description: '',
      expiration_date: '',
      file_id: null,
      file_name: null,
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || 'Unknown';
  };

  const getFileTypeInfo = (type) => FILE_TYPES.find(f => f.value === type) || FILE_TYPES[FILE_TYPES.length - 1];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'expired':
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
    }
  };

  const expiredFiles = files.filter(f => f.status === 'expired');
  const expiringFiles = files.filter(f => {
    if (!f.expiration_date || f.status === 'expired') return false;
    const expDate = new Date(f.expiration_date);
    const today = new Date();
    const daysUntil = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil > 0;
  });

  // Get days until expiration for a file
  const getDaysUntilExpiration = (expirationDate) => {
    if (!expirationDate) return null;
    const expDate = new Date(expirationDate);
    const today = new Date();
    return Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
  };

  // Quick upload file types for drivers
  const DRIVER_QUICK_UPLOADS = [
    { type: 'license', label: 'CDL License', icon: User, description: 'Upload your CDL' },
    { type: 'medical_card', label: 'Medical Certificate', icon: Stethoscope, description: 'Upload medical card' },
    { type: 'other', label: 'Receipts & Invoices', icon: FileText, description: 'Upload receipts' },
  ];

  // Handle quick upload for drivers
  const handleQuickUpload = (fileType) => {
    setQuickUploadType(fileType);
    quickUploadRef.current?.click();
  };

  const handleQuickUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !quickUploadType) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('File must be smaller than 100MB');
      return;
    }

    setUploading(true);
    try {
      // First upload the document
      const uploadRes = await driverFilesApi.uploadDocument(file);
      
      // Get the driver associated with this user's unit
      const driverForUnit = drivers.length > 0 ? drivers[0] : null;
      if (!driverForUnit) {
        toast.error('No driver found for your unit');
        return;
      }

      // Create the file record
      const typeInfo = FILE_TYPES.find(t => t.value === quickUploadType);
      await driverFilesApi.create({
        driver_id: driverForUnit.id,
        file_type: quickUploadType,
        title: `${typeInfo?.label || 'Document'} - ${new Date().toLocaleDateString()}`,
        description: `Uploaded by driver`,
        expiration_date: '',
        file_id: uploadRes.data.file_id,
        file_name: uploadRes.data.filename,
      });
      
      toast.success('Document uploaded successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setQuickUploadType(null);
      if (quickUploadRef.current) quickUploadRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="driver-files">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-manrope font-bold text-foreground">Driver Files</h1>
          <p className="text-muted-foreground mt-1">
            {isDriver ? 'View and upload your documents' : 'DOT compliant driver qualification documents'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button 
                variant="outline" 
                onClick={handleSendReminders}
                disabled={sendingReminders}
                data-testid="send-reminders-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingReminders ? 'Sending...' : 'Email Reminders'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDownloadReport}
                disabled={generatingReport}
                data-testid="download-report-btn"
              >
                <DownloadIcon className="h-4 w-4 mr-2" />
                {generatingReport ? 'Generating...' : 'Compliance Report'}
              </Button>
            </>
          )}
          {/* Driver Filter - Hide for drivers */}
          {!isDriver && (
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger className="w-[180px]" data-testid="driver-filter">
                <SelectValue placeholder="Filter by driver" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map((driver) => (
                  <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
              ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Tabs: Files / Applications */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="files">Driver Files</TabsTrigger>
            {!isDriver && <TabsTrigger value="applications" data-testid="applications-tab">Applications</TabsTrigger>}
          </TabsList>
          {activeTab === 'applications' && !isDriver && (
            <div className="flex items-center gap-2">
              {/* AI Upload Button */}
              <Dialog open={aiUploadDialogOpen} onOpenChange={setAiUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-blue-200 text-blue-600 hover:bg-blue-50" data-testid="ai-upload-application-btn">
                    <Sparkles className="h-4 w-4 mr-1" /> AI Upload
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[400px]">
                  <DialogHeader>
                    <DialogTitle className="font-manrope flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-blue-600" />
                      AI Application Upload
                    </DialogTitle>
                    <DialogDescription>
                      Upload an existing driver application (PDF) and AI will extract info to auto-create the driver profile.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {extractingInfo ? (
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                          <p className="text-sm text-muted-foreground">Analyzing application...</p>
                        </div>
                      </div>
                    ) : (
                      <DragDropZone
                        onFileSelect={(file) => handleAIApplicationUpload({ target: { files: [file] } })}
                        accept=".pdf"
                        data-testid="ai-application-file-input"
                      >
                        <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                        <p className="text-sm font-medium">Drag & drop or click to upload application</p>
                        <p className="text-xs text-muted-foreground mt-1">PDF files only, up to 10MB</p>
                      </DragDropZone>
                    )}
                    <p className="text-xs text-muted-foreground">
                      AI will extract: Name, Email, Phone, Address, License Info, SSN and auto-create a driver profile + file record.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Share Dialog */}
              <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="share-application-btn">
                    <Share2 className="h-4 w-4 mr-1" /> Share Application
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[420px]">
                  <DialogHeader>
                    <DialogTitle className="font-manrope flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-blue-600" />
                      Share Application Link
                    </DialogTitle>
                    <DialogDescription>
                      Share the driver application form with potential applicants.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Link display */}
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <Link2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-mono truncate flex-1">{applicationLink}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={copyApplicationLink}
                        data-testid="copy-link-btn"
                      >
                        {linkCopied ? <Check className="h-4 w-4 text-green-600" /> : <ClipboardCopy className="h-4 w-4" />}
                      </Button>
                    </div>

                    {/* Share options */}
                    <div className="grid grid-cols-1 gap-2">
                      <Button variant="outline" className="justify-start" onClick={handleShareViaEmail} data-testid="share-email-btn">
                        <Mail className="h-4 w-4 mr-3 text-blue-600" />
                        Share via Email
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={handleShareViaSMS} data-testid="share-sms-btn">
                        <MessageSquare className="h-4 w-4 mr-3 text-green-600" />
                        Share via SMS / Text
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={handleNativeShare} data-testid="share-native-btn">
                        <Share2 className="h-4 w-4 mr-3 text-violet-600" />
                        Share via Other Apps
                      </Button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => window.open('/apply', '_blank')} data-testid="preview-app-btn">
                      <ExternalLink className="h-4 w-4 mr-1" /> Preview Application
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <TabsContent value="files">

      {/* Driver Quick Upload Section - Only for drivers with upload permission */}
      {isDriver && canUploadFiles && (
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
          <CardHeader>
            <CardTitle className="text-lg font-manrope flex items-center gap-2 text-green-800 dark:text-green-300">
              <Upload className="h-5 w-5" />
              Quick Upload
            </CardTitle>
            <CardDescription className="text-green-700 dark:text-green-400">
              Upload your documents directly (up to 100MB per file)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {DRIVER_QUICK_UPLOADS.map((item) => (
                <Button
                  key={item.type}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-100 dark:hover:bg-green-900/50"
                  onClick={() => handleQuickUpload(item.type)}
                  disabled={uploading}
                  data-testid={`quick-upload-${item.type}`}
                >
                  <item.icon className="h-8 w-8 text-green-600" />
                  <span className="font-medium">{item.label}</span>
                  <span className="text-xs text-muted-foreground">{item.description}</span>
                </Button>
              ))}
            </div>
            <input 
              ref={quickUploadRef} 
              type="file" 
              className="hidden" 
              onChange={handleQuickUploadFile}
              accept="image/*,.pdf,.doc,.docx"
            />
            {uploading && (
              <div className="mt-4 flex items-center justify-center gap-2 text-green-700">
                <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                Uploading...
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alert Cards - Enhanced for drivers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {expiredFiles.length > 0 && (
          <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-800 dark:text-red-400">
                    {expiredFiles.length} Expired Document{expiredFiles.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-red-600 mb-2">Requires immediate renewal</p>
                  {isDriver && (
                    <div className="space-y-1">
                      {expiredFiles.slice(0, 3).map(file => {
                        const typeInfo = getFileTypeInfo(file.file_type);
                        return (
                          <div key={file.id} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                            <typeInfo.icon className="h-3 w-3" />
                            <span className="font-medium">{typeInfo.label}</span>
                            <span className="text-red-500">- EXPIRED</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {expiringFiles.length > 0 && (
          <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Clock className="h-6 w-6 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-800 dark:text-amber-400">
                    {expiringFiles.length} Expiring Soon
                  </p>
                  <p className="text-sm text-amber-600 mb-2">Within 30 days</p>
                  {isDriver && (
                    <div className="space-y-1">
                      {expiringFiles.slice(0, 3).map(file => {
                        const typeInfo = getFileTypeInfo(file.file_type);
                        const daysLeft = getDaysUntilExpiration(file.expiration_date);
                        return (
                          <div key={file.id} className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                            <typeInfo.icon className="h-3 w-3" />
                            <span className="font-medium">{typeInfo.label}</span>
                            <span className="text-amber-600">- {daysLeft} days left</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add File Button & Table */}
      <div className="flex justify-end">
        {canEdit && !isDriver && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-file-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingFile ? 'Edit' : 'Add'} Driver Document</DialogTitle>
                <DialogDescription>
                  Upload and track driver qualification documents
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Driver *</Label>
                    <Select value={formData.driver_id} onValueChange={(v) => setFormData({...formData, driver_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select driver" /></SelectTrigger>
                      <SelectContent>
                        {drivers.filter(d => d.status !== 'inactive' || d.id === formData.driver_id).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Document Type *</Label>
                    <Select value={formData.file_type} onValueChange={(v) => setFormData({...formData, file_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FILE_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            <div className="flex items-center gap-2">
                              <t.icon className="h-4 w-4" />
                              {t.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., CDL Class A License" required />
                </div>
                <div className="space-y-2">
                  <Label>Expiration Date</Label>
                  <Input type="date" value={formData.expiration_date} onChange={(e) => setFormData({...formData, expiration_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} />
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Upload Document</Label>
                  <div>
                    {!selectedDocument && !formData.file_name ? (
                      <DragDropZone
                        onFileSelect={(file) => handleFileUpload({ target: { files: [file] } })}
                        accept="*"
                        maxSize={100 * 1024 * 1024}
                        disabled={uploading}
                        className="p-4"
                        data-testid="driver-file-upload-zone"
                      >
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Drag & drop or click to upload (up to 100MB)</p>
                      </DragDropZone>
                    ) : (
                      <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <File className="h-6 w-6 text-blue-600" />
                          <span className="text-sm font-medium truncate max-w-[200px]">
                            {selectedDocument?.name || formData.file_name}
                          </span>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => {
                          setSelectedDocument(null);
                          setFormData({...formData, file_id: null, file_name: null});
                        }}>Remove</Button>
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingFile ? 'Update' : 'Add'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6"><Skeleton className="h-64 w-full" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {!isDriver && <TableHead>Driver</TableHead>}
                  <TableHead>Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Document</TableHead>
                  {canEdit && !isDriver && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isDriver ? 5 : 7} className="text-center py-8 text-muted-foreground">
                      <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No driver files found</p>
                      {isDriver && canUploadFiles && (
                        <p className="text-sm mt-2">Use the Quick Upload section above to add your documents</p>
                      )}
                    </TableCell>
                  </TableRow>
                ) : files.map((file) => {
                  const typeInfo = getFileTypeInfo(file.file_type);
                  const daysLeft = getDaysUntilExpiration(file.expiration_date);
                  const isExpiringSoon = daysLeft !== null && daysLeft <= 30 && daysLeft > 0;
                  
                  return (
                    <TableRow key={file.id} className={file.status === 'expired' ? 'bg-red-50/50 dark:bg-red-950/20' : isExpiringSoon ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}>
                      {!isDriver && (
                        <TableCell className="font-medium">
                          <button 
                            onClick={() => {
                              const driver = drivers.find(d => d.id === file.driver_id);
                              if (driver) openDriverDetails(driver);
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                          >
                            {getDriverName(file.driver_id)}
                          </button>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <typeInfo.icon className="h-4 w-4 text-blue-600" />
                          {typeInfo.label}
                        </div>
                      </TableCell>
                      <TableCell>{file.title}</TableCell>
                      <TableCell>
                        {file.expiration_date ? (
                          <div>
                            <span>{formatDate(file.expiration_date)}</span>
                            {isExpiringSoon && (
                              <span className="block text-xs text-amber-600">{daysLeft} days left</span>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(file.status)}</TableCell>
                      <TableCell>
                        {file.file_id ? (
                          <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => handleDownload(file.file_id, file.file_name)}>
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        ) : '-'}
                      </TableCell>
                      {canEdit && !isDriver && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => {
                            setEditingFile(file);
                            setFormData(file);
                            if (file.file_name) setSelectedDocument({ name: file.file_name });
                            setDialogOpen(true);
                          }}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDelete(file.id)}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      </TabsContent>

      {/* Applications Tab */}
      {!isDriver && (
        <TabsContent value="applications">
          <div className="space-y-4">
            {/* Filter bar */}
            <div className="flex items-center gap-3">
              <Select value={appStatusFilter} onValueChange={setAppStatusFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Filter status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{applications.length} application(s)</span>
            </div>

            {appLoading ? (
              <div className="p-6"><Skeleton className="h-64 w-full" /></div>
            ) : applications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserPlus className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-muted-foreground">No Applications Yet</h3>
                  <p className="text-sm text-muted-foreground mt-1">Share the application link with drivers to start receiving applications.</p>
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => setShareDialogOpen(true)}>
                    <Share2 className="h-4 w-4 mr-1" /> Share Application Link
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {applications.map(app => (
                  <Card key={app.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedApp(app); setAppDetailOpen(true); }}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-sm">{app.first_name} {app.last_name}</h4>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                              {app.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {app.email}</span>}
                              {app.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {app.phone}</span>}
                              {app.license_state && app.license_class && <span className="flex items-center gap-1"><Truck className="h-3 w-3" /> CDL {app.license_class} - {app.license_state}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(app.status)}>{app.status.replace('_', ' ')}</Badge>
                          <span className="text-xs text-muted-foreground">{new Date(app.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Application Detail Dialog */}
          <Dialog open={appDetailOpen} onOpenChange={setAppDetailOpen}>
            <DialogContent className="sm:max-w-[650px]">
              <DialogHeader>
                <DialogTitle className="font-manrope flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Application Details
                </DialogTitle>
                {selectedApp && (
                  <DialogDescription>
                    {selectedApp.first_name} {selectedApp.last_name} - Submitted {new Date(selectedApp.created_at).toLocaleDateString()}
                  </DialogDescription>
                )}
              </DialogHeader>
              {selectedApp && (
                <div className="space-y-4 pb-2">
                  {/* Status Bar */}
                  <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                    <span className="text-sm font-medium">Status:</span>
                    <Badge className={getStatusColor(selectedApp.status)}>{selectedApp.status.replace('_', ' ')}</Badge>
                    <div className="ml-auto flex gap-1">
                      {['new','in_review','approved','rejected'].map(s => (
                        <Button key={s} size="sm" variant={selectedApp.status === s ? 'default' : 'outline'} className="text-xs h-7" onClick={() => handleUpdateAppStatus(selectedApp.id, s)}>
                          {s.replace('_', ' ')}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Personal Info */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Personal Information</h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> {selectedApp.first_name} {selectedApp.middle_name} {selectedApp.last_name}</div>
                      <div><span className="text-muted-foreground">DOB:</span> {selectedApp.date_of_birth || '-'}</div>
                      <div><span className="text-muted-foreground">Email:</span> {selectedApp.email || '-'}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {selectedApp.phone || '-'}</div>
                      <div><span className="text-muted-foreground">SSN:</span> {selectedApp.ssn ? '***-**-' + selectedApp.ssn.slice(-4) : '-'}</div>
                    </div>
                  </div>

                  {/* Address */}
                  {selectedApp.current_address?.street && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Current Address</h4>
                      <p className="text-sm">{selectedApp.current_address.street}, {selectedApp.current_address.city}, {selectedApp.current_address.state} {selectedApp.current_address.zip_code}</p>
                    </div>
                  )}

                  {/* License Info */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">License Information</h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div><span className="text-muted-foreground">License #:</span> {selectedApp.license_number || '-'}</div>
                      <div><span className="text-muted-foreground">State:</span> {selectedApp.license_state || '-'}</div>
                      <div><span className="text-muted-foreground">Class:</span> {selectedApp.license_class || '-'}</div>
                      <div><span className="text-muted-foreground">Expiration:</span> {selectedApp.license_expiration || '-'}</div>
                      <div><span className="text-muted-foreground">Endorsements:</span> {selectedApp.endorsements || '-'}</div>
                      <div><span className="text-muted-foreground">Medical Card Exp:</span> {selectedApp.medical_card_expiration || '-'}</div>
                    </div>
                    {selectedApp.has_license_been_denied && <p className="text-sm text-red-600">License denied/revoked: {selectedApp.denial_details}</p>}
                    {selectedApp.has_license_been_suspended && <p className="text-sm text-red-600">License suspended: {selectedApp.suspension_details}</p>}
                  </div>

                  {/* Experience */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Experience</h4>
                    <p className="text-sm"><span className="text-muted-foreground">Years:</span> {selectedApp.years_experience || '-'}</p>
                    {selectedApp.equipment_experience?.length > 0 && (
                      <div className="flex flex-wrap gap-1">{selectedApp.equipment_experience.map(e => <Badge key={e} variant="outline" className="text-xs">{e}</Badge>)}</div>
                    )}
                  </div>

                  {/* Employment History */}
                  {selectedApp.employment_history?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Employment History ({selectedApp.employment_history.length})</h4>
                      {selectedApp.employment_history.map((emp, i) => (
                        <div key={i} className="text-sm border rounded p-2">
                          <div className="font-medium">{emp.company_name || 'Unknown'}</div>
                          <div className="text-muted-foreground text-xs">{emp.position} | {emp.from_date} - {emp.to_date || 'Present'} | {emp.city}, {emp.state}</div>
                          {emp.reason_for_leaving && <div className="text-xs mt-1">Left: {emp.reason_for_leaving}</div>}
                          <div className="flex gap-2 mt-1">{emp.dot_regulated && <Badge variant="outline" className="text-xs">DOT</Badge>}{emp.safety_sensitive && <Badge variant="outline" className="text-xs">Safety-Sensitive</Badge>}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Accidents & Violations */}
                  {(selectedApp.accidents?.length > 0 || selectedApp.violations?.length > 0) && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Accidents & Violations</h4>
                      {selectedApp.accidents?.map((acc, i) => <div key={i} className="text-sm border-l-2 border-red-400 pl-2">{acc.date} - {acc.nature}{acc.fatalities ? ' (Fatalities)' : ''}{acc.injuries ? ' (Injuries)' : ''}</div>)}
                      {selectedApp.violations?.map((v, i) => <div key={i} className="text-sm border-l-2 border-amber-400 pl-2">{v.date} - {v.charge} at {v.location}</div>)}
                    </div>
                  )}

                  {/* MVR Authorization */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">MVR Authorization</h4>
                    <div className="grid grid-cols-2 gap-x-4 text-sm">
                      <div><span className="text-muted-foreground">Signature:</span> <span className="font-serif italic">{selectedApp.mvr_signature_name || '-'}</span></div>
                      <div><span className="text-muted-foreground">Date:</span> {selectedApp.mvr_signature_date || '-'}</div>
                      <div><span className="text-muted-foreground">MVR Consent:</span> {selectedApp.mvr_consent ? <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge> : <Badge className="bg-red-100 text-red-800 text-xs">No</Badge>}</div>
                      <div><span className="text-muted-foreground">Certified True:</span> {selectedApp.certify_information_true ? <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge> : <Badge className="bg-red-100 text-red-800 text-xs">No</Badge>}</div>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter className="pt-4 border-t">
                <Button variant="destructive" size="sm" onClick={() => selectedApp && handleDeleteApp(selectedApp.id)}>Delete</Button>
                <Button variant="outline" onClick={() => setAppDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      )}
      </Tabs>

      {/* Driver Details Dialog */}
      <Dialog open={driverDetailsOpen} onOpenChange={setDriverDetailsOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Driver Details
            </DialogTitle>
            {selectedDriverDetails && (
              <DialogDescription>
                Full information for <strong>{selectedDriverDetails.name}</strong>
              </DialogDescription>
            )}
          </DialogHeader>
          
          {selectedDriverDetails && (
            <div className="space-y-6 pb-4">
                {/* Basic Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Full Name</p>
                      <p className="font-medium">{selectedDriverDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <Badge variant={selectedDriverDetails.status === 'active' ? 'default' : 'secondary'}>
                        {selectedDriverDetails.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email
                      </p>
                      <p className="font-medium">{selectedDriverDetails.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" /> Phone
                      </p>
                      <p className="font-medium">{selectedDriverDetails.phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Address
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">Street Address</p>
                      <p className="font-medium">{selectedDriverDetails.street_address || selectedDriverDetails.address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">City</p>
                      <p className="font-medium">{selectedDriverDetails.city || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">State</p>
                      <p className="font-medium">{selectedDriverDetails.state || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">ZIP Code</p>
                      <p className="font-medium">{selectedDriverDetails.zipcode || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* License Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Shield className="h-4 w-4" /> License Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">License Number</p>
                      <p className="font-medium">{selectedDriverDetails.license_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Expiry Date</p>
                      <p className="font-medium">{selectedDriverDetails.license_expiry || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Assignment & Pay */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Truck className="h-4 w-4" /> Assignment & Pay
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Assigned Unit</p>
                      <p className="font-medium">{selectedDriverDetails.assigned_unit_number || 'Not Assigned'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pay Type</p>
                      <p className="font-medium capitalize">{selectedDriverDetails.pay_type?.replace('_', ' ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pay Rate</p>
                      <p className="font-medium">
                        {selectedDriverDetails.pay_rate 
                          ? selectedDriverDetails.pay_type === 'percentage' 
                            ? `${selectedDriverDetails.pay_rate}%` 
                            : selectedDriverDetails.pay_type === 'per_mile'
                            ? `$${selectedDriverDetails.pay_rate}/mile`
                            : `$${selectedDriverDetails.pay_rate}`
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Owner Operator</p>
                      <p className="font-medium">{selectedDriverDetails.is_owner_operator ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {/* Sensitive Info */}
                <div className="space-y-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 uppercase tracking-wide flex items-center gap-2">
                    <DollarSign className="h-4 w-4" /> Sensitive Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">SSN (Last 4)</p>
                      <p className="font-medium font-mono">{selectedDriverDetails.ssn_last_four ? `***-**-${selectedDriverDetails.ssn_last_four}` : '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
          )}

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDriverDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
